<script setup>
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { useStateStore } from '@/stores/state.store';
import { AuthService } from '@/services';
import { Logger } from '../../middlewares/sentry';

const props = defineProps({
  clientId: {
    type: String,
    required: true,
  },
});

const quasar = useQuasar();
const stateStore = useStateStore();

const signUpFormRef = ref(null);

const email = ref('');
const password = ref('');
const isPwd = ref(false);

function isValidEmail(val) {
  const emailPattern =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailPattern.test(val) || 'Błędny adres email';
}

function isValidPassword(val) {
  const passwordPattern =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*()_+~`|}{[\]:;?><,./-]).{8,}$/;

  return (
    passwordPattern.test(val) ||
    'Hasło musi mieć co najmniej 8 znaków, zawierać jedną dużą literę, jedną małą literę i jeden znak specjalny'
  );
}

async function onSubmit() {
  if (email.value === '' || password.value === '') {
    quasar.notify({
      type: 'negative',
      message: `Pola nie mogą zostać puste`,
    });
    return;
  }

  try {
    const res = await AuthService.createClientPanelAccount(
      props.clientId,
      email.value,
      password.value,
    );

    if (res) {
      quasar.notify({
        type: 'info',
        message: `Utworzono klienta`,
      });
    } else {
      quasar.notify({
        type: 'negative',
        message: `Błąd w przesyle danych`,
      });
    }
  } catch (error) {
    quasar.notify({
      type: 'negative',
      message: `Błąd podczas logowania`,
    });

    Logger.warn(error);
  }

  signUpFormRef.value.resetValidation();
}
</script>

<template>
  <div class="sign-up">
    <q-form ref="signUpFormRef" class="form" @submit.prevent="onSubmit">
      <div class="input">
        <p class="label">Email</p>
        <q-input
          v-model="email"
          class="core-default-input-form input-form"
          :dark="stateStore.isBlackTheme"
          borderless
          autogrow
          lazy-rules
          :rules="[
            (val) => !!val || 'To pole nie może być puste',
            isValidEmail,
          ]"
        />
      </div>
      <div class="input">
        <p class="label">Hasło</p>
        <q-input
          v-model="password"
          class="core-default-input-form input-form"
          :dark="stateStore.isBlackTheme"
          :type="isPwd ? 'password' : 'text'"
          borderless
          lazy-rules
          :rules="[
            (val) => !!val || 'To pole nie może być puste',
            isValidPassword,
          ]"
        >
          <template #append>
            <q-icon
              :name="isPwd ? 'visibility_off' : 'visibility'"
              class="cursor-pointer"
              @click="isPwd = !isPwd"
            />
          </template>
        </q-input>
      </div>
      <button class="form-button core-default-form-button" type="submit">
        Utwórz konto
      </button>
    </q-form>
  </div>
</template>

<style lang="scss" scoped>
.sign-up {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 50%;
  margin: auto;
  padding: 50px 0;
  background-color: #fffbf8;
  border: 2px solid #84491f;
  border-radius: 20px;

  .form {
    display: flex;
    flex-direction: column;
    gap: 30px;

    .input {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;

      .input-form {
        width: 300px;
      }
    }
  }

  .form-button {
    margin-top: 20px;
  }
}
</style>
